'use client';

import React, { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { createClient } from '@/lib/supabase/client';

interface Story {
  id: string;
  company_name: string;
  industry: string;
  challenge: string;
  solution: string;
  results: string;
  testimonial: string;
  image_url: string;
  sponsor_logo_url: string | null;
}

const SuccessStories = () => {
  const { t } = useTranslation();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('success_stories')
        .select(`
          *,
          sponsors:sponsor_id (
            name,
            logo_url,
            website
          )
        `)
        .eq('is_featured', true)
        .order('display_order', { ascending: true })
        .limit(3);

      if (error) {
        console.error('Error fetching success stories:', error);
      } else if (data) {
        setStories(data);
      }
      setLoading(false);
    };

    fetchStories();
  }, []);

  if (loading) {
    return (
      <section className="py-20 px-6 bg-gradient-to-b from-background to-card/30">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading success stories...</p>
        </div>
      </section>
    );
  }

  if (stories.length === 0) {
    return null; // Don't show section if no stories
  }

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-background to-card/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
            {t('successStories')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('successStoriesDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {stories.map((story) => {
            const resultsArray = story.results ? story.results.split(',').map(r => r.trim()) : [];

            return (
              <div
                key={story.id}
                className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Image Section */}
                <div className="relative h-56 overflow-hidden bg-muted">
                  {story.image_url && (
                    <AppImage
                      src={story.image_url}
                      alt={story.company_name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                  {/* Company Info Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-headline font-bold text-white mb-1">
                      {story.company_name}
                    </h3>
                    <p className="text-sm text-white/90 flex items-center gap-2">
                      <Icon name="BuildingOfficeIcon" size={14} variant="outline" />
                      {story.industry}
                    </p>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6 space-y-5">
                  {/* Challenge */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="ExclamationTriangleIcon" size={18} variant="outline" className="text-warning" />
                      <h4 className="text-sm font-bold text-foreground">{t('challenge')}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {story.challenge}
                    </p>
                  </div>

                  {/* Solution */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="LightBulbIcon" size={18} variant="outline" className="text-accent" />
                      <h4 className="text-sm font-bold text-foreground">{t('solution')}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {story.solution}
                    </p>
                  </div>

                  {/* Results */}
                  {resultsArray.length > 0 && (
                    <div className="pt-4 border-t border-border">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="ChartBarIcon" size={18} variant="outline" className="text-success" />
                        <h4 className="text-sm font-bold text-foreground">{t('results')}</h4>
                      </div>
                      <div className="space-y-2">
                        {resultsArray.map((result, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Icon
                              name="CheckCircleIcon"
                              size={16}
                              variant="solid"
                              className="text-success flex-shrink-0"
                            />
                            <span className="text-sm text-muted-foreground">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Testimonial */}
                  {story.testimonial && (
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm italic text-muted-foreground">
                        "{story.testimonial}"
                      </p>
                    </div>
                  )}

                  {/* Sponsor Section */}
                  {story.sponsor_logo_url && (
                    <div className="pt-4 mt-4 border-t border-border">
                      <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Proudly Sponsored By</p>
                      <div className="flex flex-col items-center justify-center bg-gradient-to-br from-muted/40 to-muted/20 rounded-xl p-4 border border-border/50">
                        <AppImage
                          src={story.sponsor_logo_url}
                          alt={(story as any).sponsors?.name || 'Sponsor'}
                          className="h-12 w-auto object-contain mb-2"
                        />
                        {(story as any).sponsors?.name && (
                          <p className="text-sm font-semibold text-foreground text-center">
                            {(story as any).sponsors.name}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">Ready to write your success story?</p>
          <a
            href="/manufacturer-directory"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <span>Start Sourcing Now</span>
            <Icon name="ArrowRightIcon" size={18} variant="outline" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default SuccessStories;